import { useMemo, useState } from 'react'
import {
  Link2, Copy, Trash2, Plus, Search, Building2, Clock, Repeat, Infinity as InfinityIcon, Check, Download,
} from 'lucide-react'
import { dialog } from '../../../services/dialog'
import api from '../../../services/api'
import { copyToClipboard } from '../../../utils/clipboard'
import { useToastStore } from '../../../stores/toastStore'
import { Pagination, usePaginatedSlice } from '../../../components/ui/pagination'

interface Dept {
  id: string
  name: string
}
interface Invite {
  id: string
  code: string
  departmentId: string | null
  maxUses: number | null
  uses: number
  usedAt: string | null
  expiresAt: string | null
  createdAt: string | null
}

type DerivedStatus = 'active' | 'used' | 'expired'

const EXPIRY_PRESETS: { label: string; hours: number | null }[] = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 24 * 7 },
  { label: '30 days', hours: 24 * 30 },
  { label: 'Never', hours: null },
]

// `null` = unlimited.
const USES_PRESETS: { label: string; value: number | null }[] = [
  { label: '1', value: 1 },
  { label: '5', value: 5 },
  { label: '25', value: 25 },
  { label: '100', value: 100 },
  { label: '∞', value: null },
]

const STATUS_TABS: { value: DerivedStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'used', label: 'Used up' },
  { value: 'expired', label: 'Expired' },
]

function deriveStatus(inv: Invite): DerivedStatus {
  const exhausted = inv.maxUses != null && (inv.uses ?? 0) >= inv.maxUses
  if (exhausted) return 'used'
  if (inv.expiresAt && new Date(inv.expiresAt) < new Date()) return 'expired'
  return 'active'
}

// Compact, human "when": inside 24h relative, inside 7d weekday, else date.
// Tooltip carries the full timestamp.
function formatWhen(iso: string | null | undefined): { short: string; full: string } {
  if (!iso) return { short: 'Never', full: 'Never' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { short: '—', full: String(iso) }
  const full = d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
  const diff = d.getTime() - Date.now()
  const absMin = Math.round(Math.abs(diff) / 60_000)
  const absH = Math.round(Math.abs(diff) / 3_600_000)
  const absD = Math.round(Math.abs(diff) / 86_400_000)
  const ago = (s: string) => (diff < 0 ? `${s} ago` : `in ${s}`)
  let short: string
  if (absMin < 1) short = 'just now'
  else if (absMin < 60) short = ago(`${absMin}m`)
  else if (absH < 24) short = ago(`${absH}h`)
  else if (absD < 7) short = ago(`${absD}d`)
  else short = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })
  return { short, full }
}

export default function InvitesTab({
  serverId,
  invites,
  departments,
  onChanged,
}: {
  serverId: string
  invites: Invite[]
  departments: Dept[]
  onChanged: () => Promise<void> | void
}) {
  // ── create form ──
  const [departmentId, setDepartmentId] = useState('')
  const [expiresHours, setExpiresHours] = useState<number | null>(24)
  const [maxUses, setMaxUses] = useState<number | null>(1)
  const [creating, setCreating] = useState(false)

  // ── filters ──
  const [statusFilter, setStatusFilter] = useState<DerivedStatus | 'all'>('all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const sortedDepartments = useMemo(
    () => [...departments].sort((a, b) => a.name.localeCompare(b.name)),
    [departments],
  )
  const deptName = useMemo(() => {
    const m = new Map(departments.map((d) => [d.id, d.name]))
    return (id: string | null) => (id ? m.get(id) ?? null : null)
  }, [departments])

  // status counts for the segmented control badges
  const counts = useMemo(() => {
    const c = { all: invites.length, active: 0, used: 0, expired: 0 }
    for (const inv of invites) c[deriveStatus(inv)]++
    return c
  }, [invites])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return invites.filter((inv) => {
      if (statusFilter !== 'all' && deriveStatus(inv) !== statusFilter) return false
      if (q) {
        const dn = (deptName(inv.departmentId) ?? '').toLowerCase()
        if (!inv.code.toLowerCase().includes(q) && !dn.includes(q)) return false
      }
      return true
    })
  }, [invites, statusFilter, search, deptName])

  const paged = usePaginatedSlice(filtered, page, pageSize)

  const toast = useToastStore.getState

  const handleCreate = async () => {
    setCreating(true)
    try {
      const body: any = { maxUses }
      if (expiresHours != null) {
        body.expiresAt = new Date(Date.now() + expiresHours * 3_600_000).toISOString()
      }
      if (departmentId) body.departmentId = departmentId
      const { data: invite } = await api.post(`/servers/${serverId}/user-invites`, body)
      const usesLabel = maxUses == null ? 'unlimited uses' : `${maxUses} use${maxUses === 1 ? '' : 's'}`
      const dn = departmentId ? ` · ${deptName(departmentId) ?? 'department'}` : ''
      toast().showToast({
        id: `invite-created-${invite.id}`,
        type: 'message',
        title: 'Invite created',
        body: `${usesLabel}${dn}`,
      })
      await onChanged()
    } catch (e: any) {
      toast().showToast({
        id: `invite-create-error-${Date.now()}`,
        type: 'error',
        title: 'Failed to create invite',
        body: e?.response?.data?.error,
      })
    } finally {
      setCreating(false)
    }
  }

  const copyLink = async (code: string, id: string) => {
    const link = `${window.location.origin}/register?invite=${code}`
    await copyToClipboard(link)
    toast().showToast({ id: `invite-link-copied-${id}-${Date.now()}`, type: 'message', title: 'Link copied', body: link })
  }

  const remove = async (id: string) => {
    if (!(await dialog.confirm({ message: 'Delete this invite? The link stops working immediately.', tone: 'destructive', confirmLabel: 'Delete' }))) return
    try {
      await api.delete(`/servers/${serverId}/user-invites/${id}`)
      await onChanged()
    } catch (e: any) {
      toast().showToast({ id: `invite-del-err-${Date.now()}`, type: 'error', title: 'Failed to delete invite', body: e?.response?.data?.error })
    }
  }

  // Export the CURRENTLY shown invites (status filter + search applied)
  // as a CSV. BOM-prefixed UTF-8 so Excel renders non-ASCII department
  // names; CRLF line endings per the CSV convention.
  const exportCsv = () => {
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v)
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
    }
    const statusLabel: Record<DerivedStatus, string> = { active: 'Active', used: 'Used up', expired: 'Expired' }
    const header = ['Code', 'Link', 'Department', 'Status', 'Uses', 'Max uses', 'Created', 'Expires', 'Last used']
    const rows = filtered.map((inv) => [
      inv.code,
      `${window.location.origin}/register?invite=${inv.code}`,
      deptName(inv.departmentId) ?? '',
      statusLabel[deriveStatus(inv)],
      inv.uses ?? 0,
      inv.maxUses == null ? 'Unlimited' : inv.maxUses,
      inv.createdAt ?? '',
      inv.expiresAt ?? '',
      inv.usedAt ?? '',
    ])
    const csv = [header, ...rows].map((r) => r.map(esc).join(',')).join('\r\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `registration-invites-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const fieldCls = 'w-full bg-muted text-foreground rounded-md px-3 py-2 text-sm border border-border focus:outline-none focus:border-primary transition'

  return (
    <div className="space-y-5">
      {/* ── Create panel ── */}
      <div className="rounded-xl ring-1 ring-border bg-card/40 overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Link2 size={15} />
          </span>
          <div>
            <div className="text-sm font-semibold text-foreground">New registration invite</div>
            <div className="text-[11px] text-muted-foreground">Share the link — anyone who opens it can create an account on this server.</div>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Department */}
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground mb-1.5">
              <Building2 size={12} /> Department
            </label>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} className={fieldCls}>
              <option value="">No department</option>
              {sortedDepartments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
              {departmentId ? `Registrants join “${deptName(departmentId) ?? ''}”.` : 'Registrants join with no department.'}
            </p>
          </div>

          {/* Uses */}
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground mb-1.5">
              <Repeat size={12} /> Uses
            </label>
            <div className="flex flex-wrap gap-1.5">
              {USES_PRESETS.map((p) => {
                const active = maxUses === p.value
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setMaxUses(p.value)}
                    className={`min-w-[34px] px-2.5 py-1 text-xs rounded-md transition ${
                      active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground border border-border'
                    }`}
                  >
                    {p.label}
                  </button>
                )
              })}
              <input
                type="number"
                min={1}
                value={maxUses ?? ''}
                onChange={(e) => setMaxUses(e.target.value === '' ? null : Math.max(1, parseInt(e.target.value) || 1))}
                placeholder="Custom"
                className="w-20 bg-muted text-foreground rounded-md px-2 py-1 text-xs border border-border focus:outline-none focus:border-primary"
                title="Custom number of uses (empty = unlimited)"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug flex items-center gap-1">
              {maxUses == null
                ? <><InfinityIcon size={11} /> Unlimited registrations.</>
                : `Good for ${maxUses} registration${maxUses === 1 ? '' : 's'}.`}
            </p>
          </div>

          {/* Expires */}
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground mb-1.5">
              <Clock size={12} /> Expires
            </label>
            <div className="flex flex-wrap gap-1.5">
              {EXPIRY_PRESETS.map((p) => {
                const active = expiresHours === p.hours
                return (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => setExpiresHours(p.hours)}
                    className={`px-2.5 py-1 text-xs rounded-md transition ${
                      active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground border border-border'
                    }`}
                  >
                    {p.label}
                  </button>
                )
              })}
            </div>
            <p className="text-[10px] text-muted-foreground mt-1 leading-snug">
              {expiresHours == null ? 'Link never expires.' : 'Link expires after the chosen window.'}
            </p>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-border bg-muted/30 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            {maxUses == null ? 'Multi-use link.' : maxUses === 1 ? 'Single-use link.' : `Multi-use link (${maxUses}).`}
          </span>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <Plus size={15} />{creating ? 'Creating…' : 'Create invite'}
          </button>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg bg-muted p-0.5 ring-1 ring-border">
          {STATUS_TABS.map((t) => {
            const active = statusFilter === t.value
            const n = counts[t.value]
            return (
              <button
                key={t.value}
                onClick={() => { setStatusFilter(t.value); setPage(1) }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition flex items-center gap-1.5 ${
                  active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
                <span className={`tabular-nums text-[10px] px-1.5 py-0.5 rounded-full ${active ? 'bg-primary/15 text-primary' : 'bg-background/60 text-muted-foreground'}`}>{n}</span>
              </button>
            )
          })}
        </div>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-muted text-muted-foreground hover:text-foreground border border-border transition disabled:opacity-50"
          title={`Download the ${filtered.length} shown invite${filtered.length === 1 ? '' : 's'} as a CSV file`}
        >
          <Download size={14} /> Export CSV
        </button>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search code or department…"
            className="w-64 bg-muted text-foreground rounded-md pl-8 pr-3 py-1.5 text-sm border border-border focus:outline-none focus:border-primary transition"
          />
        </div>
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <div className="rounded-xl ring-1 ring-border bg-card/40 px-6 py-14 text-center">
          <Link2 size={28} className="mx-auto text-muted-foreground/50 mb-3" />
          <div className="text-sm text-foreground font-medium">
            {invites.length === 0 ? 'No invites yet' : 'No invites match your filters'}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {invites.length === 0 ? 'Create one above to start onboarding people.' : 'Try a different status or clear the search.'}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-muted-foreground text-left">
                <th className="px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Code</th>
                <th className="px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Department</th>
                <th className="px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Uses</th>
                <th className="px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Created</th>
                <th className="px-4 py-2.5 font-medium text-xs uppercase tracking-wider">Expires</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {paged.map((inv) => {
                const status = deriveStatus(inv)
                const dn = deptName(inv.departmentId)
                const created = formatWhen(inv.createdAt)
                const expires = formatWhen(inv.expiresAt)
                const unlimited = inv.maxUses == null
                const usesPct = unlimited ? 0 : Math.min(100, Math.round(((inv.uses ?? 0) / Math.max(1, inv.maxUses!)) * 100))
                return (
                  <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-accent/40 transition group">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => copyLink(inv.code, inv.id)}
                        className="font-mono text-xs text-foreground/90 hover:text-primary transition inline-flex items-center gap-1.5"
                        title="Copy registration link"
                      >
                        {inv.code.slice(0, 8)}…
                        <Copy size={11} className="opacity-0 group-hover:opacity-60 transition" />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {dn
                        ? <span className="text-foreground">{dn}</span>
                        : inv.departmentId
                          ? <span className="text-muted-foreground italic text-xs">Unknown</span>
                          : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="tabular-nums text-foreground/90 text-xs">
                          {inv.uses ?? 0}{unlimited ? '' : ` / ${inv.maxUses}`}
                        </span>
                        {unlimited ? (
                          <InfinityIcon size={13} className="text-muted-foreground" />
                        ) : (
                          <span className="w-12 h-1.5 rounded-full bg-muted overflow-hidden">
                            <span
                              className={`block h-full rounded-full ${usesPct >= 100 ? 'bg-muted-foreground' : 'bg-primary'}`}
                              style={{ width: `${usesPct}%` }}
                            />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusChip status={status} /></td>
                    <td className="px-4 py-3 text-muted-foreground text-xs tabular-nums whitespace-nowrap" title={created.full}>{created.short}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs tabular-nums whitespace-nowrap" title={expires.full}>{expires.short}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {status === 'active' && (
                          <button
                            onClick={() => copyLink(inv.code, inv.id)}
                            className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent transition"
                            title="Copy link"
                          >
                            <Copy size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => remove(inv.id)}
                          className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {filtered.length > pageSize && (
        <Pagination
          total={filtered.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(s) => { setPageSize(s); setPage(1) }}
        />
      )}
    </div>
  )
}

function StatusChip({ status }: { status: DerivedStatus }) {
  const map: Record<DerivedStatus, { label: string; cls: string; dot: string }> = {
    active: { label: 'Active', cls: 'bg-emerald-500/15 text-emerald-500', dot: 'bg-emerald-500' },
    used: { label: 'Used up', cls: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
    expired: { label: 'Expired', cls: 'bg-rose-500/15 text-rose-500', dot: 'bg-rose-500' },
  }
  const s = map[status]
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${s.cls}`}>
      {status === 'active'
        ? <Check size={11} />
        : <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
      {s.label}
    </span>
  )
}
